import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Save, Webhook, CheckCircle2, XCircle, Ticket, Trash2 } from "lucide-react";

export const PlatformTab = ({
  pfields, pvalues, setPvalues, busy, onSave,
  whTest, whBusy, onSendTestWebhook,
  coupons, couponType, setCouponType, couponEmail, setCouponEmail,
  couponBusy, onGenerateCoupon, lastCoupon, onToggleCoupon, onDeleteCoupon,
}) => (
  <div className="max-w-2xl">
    <div className="flex justify-between items-center mb-4">
      <div>
        <h2 className="text-xl font-heading font-medium text-ink">Platform Credentials & Fees</h2>
        <p className="text-sm text-mute mt-1">These belong to the platform. Secret keys show masked; leave blank/unchanged to keep the current value. Landlords receive deposit & rent into their own accounts.</p>
      </div>
      <button data-testid="save-platform-btn" onClick={onSave} disabled={busy} className="bg-forest text-white rounded-lg px-4 py-2 text-sm hover:bg-forest-hover flex items-center gap-1.5 disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>
    </div>
    <div className="bg-white border border-line rounded-xl p-6 space-y-4">
      {pfields.map((f) => (
        <div key={f.key}>
          <div className="flex items-center justify-between">
            <Label>{f.label}</Label>
            <span className={`text-xs ${f.configured ? "text-emerald-600" : "text-amber-600"}`}>{f.configured ? (f.source === "custom" ? "custom" : "from .env") : "not set"}</span>
          </div>
          <Input
            data-testid={`platform-${f.key}`}
            type={f.type === "number" ? "number" : "text"}
            value={pvalues[f.key] ?? ""}
            onChange={(e) => setPvalues({ ...pvalues, [f.key]: e.target.value })}
            placeholder={f.secret ? (f.display || "Not set") : ""}
            className="mt-1 border-line"
          />
        </div>
      ))}
    </div>

    {/* Stripe webhook self-test */}
    <div data-testid="webhook-test-card" className="bg-white border border-line rounded-xl p-6 mt-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-heading font-medium text-ink flex items-center gap-2"><Webhook className="h-4 w-4 text-forest" /> Stripe webhook test</h3>
          <p className="text-sm text-mute mt-1">Sends a harmless, signed test event to this site's <code className="text-xs">/api/webhook/stripe</code> and checks it responds. No user or subscription is affected.</p>
        </div>
        <button data-testid="send-test-webhook-btn" onClick={onSendTestWebhook} disabled={whBusy} className="shrink-0 border border-forest text-forest rounded-lg px-4 py-2 text-sm hover:bg-forest/5 flex items-center gap-1.5 disabled:opacity-60">
          {whBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Webhook className="h-4 w-4" />} Send test webhook
        </button>
      </div>
      {whTest && (
        <div data-testid="webhook-test-result" className={`mt-4 rounded-lg border p-4 text-sm ${whTest.enforced ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
          <div className="flex items-center gap-2 font-medium">
            {whTest.enforced ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-amber-600" />}
            <span className={whTest.enforced ? "text-emerald-800" : "text-amber-800"}>{whTest.enforced ? "Signature verification ENFORCED" : "Attention"}</span>
            {typeof whTest.status_code !== "undefined" && <span className="text-xs text-mute">signed HTTP {whTest.status_code}</span>}
            {typeof whTest.unsigned_status !== "undefined" && <span className="text-xs text-mute">unsigned HTTP {whTest.unsigned_status}</span>}
          </div>
          <p className="mt-1 text-ink">{whTest.message}</p>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-mute">
            <span>Signing secret: <span className="font-medium text-ink">{whTest.webhook_secret_configured ? "configured" : "not set"}</span></span>
            {typeof whTest.unsigned_rejected !== "undefined" && <span>Unsigned rejected: <span className="font-medium text-ink">{whTest.unsigned_rejected ? "yes" : "no"}</span></span>}
            {whTest.stripe_key_mode && <span>Stripe key: <span className="font-medium text-ink">{whTest.stripe_key_mode}</span></span>}
            {whTest.endpoint && <span className="truncate">Endpoint: <span className="font-medium text-ink">{whTest.endpoint}</span></span>}
          </div>
        </div>
      )}
    </div>

    {/* Coupons */}
    <div data-testid="coupons-card" className="bg-white border border-line rounded-xl p-6 mt-5">
      <div className="flex items-center gap-2"><Ticket className="h-4 w-4 text-forest" /><h3 className="font-heading font-medium text-ink">Coupon codes</h3></div>
      <p className="text-sm text-mute mt-1">Generate single-use codes. Landlord codes grant free Pro (a free month, or ongoing) — the platform % fee still applies. Renter codes waive one application fee.</p>
      <div className="mt-4 grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
        <div>
          <Label>Coupon type</Label>
          <select data-testid="coupon-type" value={couponType} onChange={(e) => setCouponType(e.target.value)} className="mt-1 w-full border border-line rounded-lg px-3 py-2 bg-white text-sm">
            <option value="landlord_free_month">Landlord — Free month of Pro</option>
            <option value="landlord_free_pro">Landlord — Free Pro (ongoing)</option>
            <option value="renter_application_fee">Renter — Waive application fee</option>
          </select>
        </div>
        <div>
          <Label>Email to (optional)</Label>
          <Input data-testid="coupon-email" type="email" value={couponEmail} onChange={(e) => setCouponEmail(e.target.value)} placeholder="recipient@email.com" className="mt-1 border-line" />
        </div>
        <button data-testid="generate-coupon-btn" onClick={onGenerateCoupon} disabled={couponBusy} className="bg-forest text-white rounded-lg px-4 py-2 text-sm hover:bg-forest-hover flex items-center gap-1.5 disabled:opacity-60 h-[38px]">
          {couponBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ticket className="h-4 w-4" />} Generate
        </button>
      </div>

      {lastCoupon && (
        <div data-testid="coupon-generated" className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-center justify-between gap-3">
          <div>
            <p className="text-xs text-emerald-700">New code {lastCoupon.emailed ? "(emailed)" : ""}</p>
            <p className="font-mono font-semibold text-ink tracking-wide">{lastCoupon.code}</p>
          </div>
          <button data-testid="copy-coupon-btn" onClick={() => { navigator.clipboard?.writeText(lastCoupon.code); toast.success("Code copied"); }} className="text-xs border border-emerald-300 text-emerald-700 rounded-lg px-3 py-1.5 hover:bg-emerald-100">Copy</button>
        </div>
      )}

      <div className="mt-5">
        {coupons.length === 0 ? (
          <p data-testid="coupons-empty" className="text-sm text-mute">No coupons generated yet.</p>
        ) : (
          <div className="border border-line rounded-lg divide-y divide-line overflow-hidden">
            {coupons.map((c) => (
              <div key={c.id} data-testid="coupon-row" className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
                <div className="min-w-0">
                  <p className="font-mono font-medium text-ink truncate">{c.code}</p>
                  <p className="text-xs text-mute truncate">
                    {c.type === "landlord_free_month" ? "Landlord · Free month" : c.type === "landlord_free_pro" ? "Landlord · Free Pro" : "Renter · App fee"}
                    {c.status === "redeemed" && c.redeemed_by_email ? ` · used by ${c.redeemed_by_email}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-xs font-semibold rounded-full px-2.5 py-0.5 border ${c.status === "active" ? "text-emerald-700 bg-emerald-50 border-emerald-200" : c.status === "redeemed" ? "text-stone-600 bg-stone-100 border-stone-200" : "text-amber-700 bg-amber-50 border-amber-200"}`}>{c.status}</span>
                  {c.status !== "redeemed" && (
                    <button data-testid="coupon-toggle-btn" onClick={() => onToggleCoupon(c)} className="text-xs border border-line rounded-lg px-2.5 py-1 hover:bg-bone">{c.status === "active" ? "Revoke" : "Reactivate"}</button>
                  )}
                  <button data-testid="coupon-delete-btn" onClick={() => onDeleteCoupon(c)} className="text-mute hover:text-red-600" title="Delete"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);
